import { t } from "@/lib/i18n/interpolate";
import {
  type BudgetLabels,
  budgetTable,
  type Format,
  targetLine,
  wordTargets,
  wordTotal,
} from "@/lib/tool-kit/business-templates/budget";
import type {
  DocBlock,
  DocSection,
  DocumentModel,
} from "@/lib/tool-kit/document";
import {
  paragraphsOrPrompt,
  splitItems,
} from "@/lib/tool-kit/essay-templates/text";

/**
 * A business case analysis outline in the sequence case-method courses commonly teach:
 * situation and facts, the problem, analysis, alternatives, recommendation, and
 * implementation. There is no single official format, so the sections, shares and
 * frameworks here are a starting point and an instructor's own template wins.
 *
 * The alternatives are laid against the writer's criteria as a blank scoring table: the
 * tool lays out the comparison and leaves the judgement to the writer.
 */

export const SECTIONS = [
  "situation",
  "problem",
  "analysis",
  "alternatives",
  "recommendation",
  "implementation",
] as const;
export type Section = (typeof SECTIONS)[number];

/** Percent of the word count per section. Sums to 100. */
export const SHARES: Record<Section, number> = {
  situation: 10,
  problem: 8,
  analysis: 30,
  alternatives: 22,
  recommendation: 15,
  implementation: 15,
};

export const FRAMEWORKS = [
  "swot",
  "pestle",
  "forces",
  "financial",
  "stakeholders",
] as const;
export type Framework = (typeof FRAMEWORKS)[number];

export const MIN_CASE_WORDS = 300;
export const MAX_CASE_WORDS = 20_000;
/** At most this many criteria become table columns. */
export const MAX_CRITERIA = 6;
/** Criteria offered when the writer has not listed any. */
export const DEFAULT_CRITERIA_COUNT = 4;

export interface CaseInput {
  title: string;
  /** Who must decide, and at what organization. */
  decisionMaker: string;
  /** The decision to be made, as a question. */
  decision: string;
  /** One fact per line. */
  facts: string;
  problem: string;
  /** One alternative per line. */
  alternatives: string;
  /** One criterion per line. */
  criteria: string;
  recommendation: string;
  frameworks: Record<Framework, boolean>;
  words: string;
}

export interface CaseLabels {
  defaultTitle: string;
  titleWithName: string;
  subtitle: string;
  budgetHeading: string;
  budgetNote: string;
  budget: BudgetLabels;
  sectionNames: Record<Section, string>;
  tips: Record<Section, string>;
  target: string;
  targetShare: string;
  fields: { decisionMaker: string; decision: string };
  prompts: {
    situation: string;
    facts: string;
    problem: string;
    noFramework: string;
    alternatives: string;
    needTwo: string;
    recommendation: string;
    actions: string;
    risks: string;
    measures: string;
  };
  frameworkNames: Record<Framework, string>;
  frameworkPrompts: Record<Framework, string>;
  tableHeads: { alternative: string; actions: readonly string[] };
  defaultCriteria: readonly string[];
  checklistHeading: string;
  checklist: readonly string[];
}

export function caseWordTotal(raw: string): number | null {
  return wordTotal(raw, MIN_CASE_WORDS, MAX_CASE_WORDS);
}

/** The criteria that head the scoring table: the writer's own, or the defaults. */
export function criteriaColumns(
  input: Pick<CaseInput, "criteria">,
  defaults: readonly string[]
): string[] {
  const own = splitItems(input.criteria).slice(0, MAX_CRITERIA);
  return own.length > 0 ? own : [...defaults];
}

function situationBlocks(input: CaseInput, labels: CaseLabels): DocBlock[] {
  const fields = [
    [labels.fields.decisionMaker, input.decisionMaker],
    [labels.fields.decision, input.decision],
  ]
    .filter(([, value]) => value.trim() !== "")
    .map(([label, value]) => ({ label, value: value.trim() }));
  const facts = splitItems(input.facts);
  return [
    fields.length > 0
      ? { kind: "fields", fields }
      : { kind: "prompt", text: labels.prompts.situation },
    facts.length > 0
      ? {
          kind: "list",
          list: { style: "bullet", items: facts.map((text) => ({ text })) },
        }
      : { kind: "prompt", text: labels.prompts.facts },
  ];
}

function analysisBlocks(input: CaseInput, labels: CaseLabels): DocBlock[] {
  const chosen = FRAMEWORKS.filter((key) => input.frameworks[key]);
  if (chosen.length === 0) {
    return [{ kind: "prompt", text: labels.prompts.noFramework }];
  }
  return chosen.flatMap((key): DocBlock[] => [
    { kind: "subheading", text: labels.frameworkNames[key] },
    { kind: "prompt", text: labels.frameworkPrompts[key] },
  ]);
}

function alternativesBlocks(input: CaseInput, labels: CaseLabels): DocBlock[] {
  const alternatives = splitItems(input.alternatives);
  if (alternatives.length === 0) {
    return [{ kind: "prompt", text: labels.prompts.alternatives }];
  }
  const criteria = criteriaColumns(input, labels.defaultCriteria);
  const blocks: DocBlock[] = [
    {
      kind: "table",
      head: [labels.tableHeads.alternative, ...criteria],
      rows: alternatives.map((name) => [name, ...criteria.map(() => "")]),
    },
  ];
  if (alternatives.length < 2) {
    blocks.push({ kind: "prompt", text: labels.prompts.needTwo });
  }
  return blocks;
}

function implementationBlocks(labels: CaseLabels): DocBlock[] {
  return [
    {
      kind: "table",
      head: labels.tableHeads.actions,
      rows: [0, 1, 2].map(() => labels.tableHeads.actions.map(() => "")),
    },
    { kind: "prompt", text: labels.prompts.actions },
    { kind: "prompt", text: labels.prompts.risks },
    { kind: "prompt", text: labels.prompts.measures },
  ];
}

function sectionBody(
  section: Section,
  input: CaseInput,
  labels: CaseLabels
): DocBlock[] {
  switch (section) {
    case "situation":
      return situationBlocks(input, labels);
    case "problem":
      return paragraphsOrPrompt(input.problem, labels.prompts.problem);
    case "analysis":
      return analysisBlocks(input, labels);
    case "alternatives":
      return alternativesBlocks(input, labels);
    case "recommendation":
      return paragraphsOrPrompt(
        input.recommendation,
        labels.prompts.recommendation
      );
    default:
      return implementationBlocks(labels);
  }
}

export function buildCaseAnalysis(
  input: CaseInput,
  labels: CaseLabels,
  fmt: Format
): DocumentModel {
  const total = caseWordTotal(input.words);
  const targets = wordTargets(SECTIONS, SHARES, total);
  const title = input.title.trim();
  const sections: DocSection[] = SECTIONS.map((section) => ({
    heading: labels.sectionNames[section],
    blocks: [
      {
        kind: "guidance",
        text: `${targetLine(SHARES[section], targets?.[section] ?? null, { withWords: labels.target, shareOnly: labels.targetShare }, fmt)} ${labels.tips[section]}`,
      },
      ...sectionBody(section, input, labels),
    ],
  }));
  return {
    title: title
      ? t(labels.titleWithName, { name: title })
      : labels.defaultTitle,
    subtitle: labels.subtitle,
    sections: [
      {
        heading: labels.budgetHeading,
        blocks: [
          { kind: "guidance", text: labels.budgetNote },
          budgetTable(
            SECTIONS,
            labels.sectionNames,
            SHARES,
            targets,
            labels.budget,
            fmt
          ),
        ],
      },
      ...sections,
      {
        heading: labels.checklistHeading,
        blocks: [
          {
            kind: "checklist",
            items: labels.checklist.map((text) => ({ text })),
          },
        ],
      },
    ],
  };
}
